// Unified gaming system storage for all games
const STORAGE_KEY = "noAdsCooking_gameProgress";
const defaultProgress = {
    totalScore: 0,
    level: 1,
    badges: [],
    quiz: {
        highScore: 0,
        currentStreak: 0,
        longestStreak: 0,
        gamesPlayed: 0,
        correctAnswers: 0,
        totalQuestions: 0,
        termsLearned: [],
    },
    memory: {
        gamesPlayed: 0,
        bestTime: 0,
        bestMoves: 0,
        perfectGames: 0,
        techniquesLearned: [],
    },
};
export function getGameProgress() {
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (!stored) {
            // Check for old quiz storage and migrate
            const oldQuizData = localStorage.getItem("noAdsCooking_quizProgress");
            if (oldQuizData) {
                const oldData = JSON.parse(oldQuizData);
                const migrated = Object.assign(Object.assign({}, defaultProgress), { totalScore: oldData.totalScore || 0, level: oldData.level || 1, badges: oldData.badges || [], quiz: {
                        highScore: oldData.highScore || 0,
                        currentStreak: oldData.currentStreak || 0,
                        longestStreak: oldData.longestStreak || 0,
                        gamesPlayed: oldData.gamesPlayed || 0,
                        correctAnswers: oldData.correctAnswers || 0,
                        totalQuestions: oldData.totalQuestions || 0,
                        termsLearned: oldData.termsLearned || [],
                    } });
                saveGameProgress(migrated);
                return migrated;
            }
            return defaultProgress;
        }
        return Object.assign(Object.assign({}, defaultProgress), JSON.parse(stored));
    }
    catch (error) {
        console.error("Error loading game progress:", error);
        return defaultProgress;
    }
}
export function saveGameProgress(progress) {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(progress));
    }
    catch (error) {
        console.error("Error saving game progress:", error);
    }
}
export function updateGameProgress(updates) {
    const current = getGameProgress();
    const updated = Object.assign(Object.assign({}, current), updates);
    saveGameProgress(updated);
    return updated;
}
export const allBadges = [
    // Universal badges
    {
        id: "first-game",
        name: "Getting Started",
        description: "Play your first game",
        icon: "🌟",
        game: "all",
        requirement: (p) => p.quiz.gamesPlayed + p.memory.gamesPlayed >= 1,
    },
    {
        id: "level-5",
        name: "Rising Chef",
        description: "Reach level 5",
        icon: "👨‍🍳",
        game: "all",
        requirement: (p) => p.level >= 5,
    },
    {
        id: "level-10",
        name: "Master Chef",
        description: "Reach level 10",
        icon: "👑",
        game: "all",
        requirement: (p) => p.level >= 10,
    },
    {
        id: "multi-gamer",
        name: "Multi-Talented",
        description: "Play both Quiz and Memory Match",
        icon: "🎮",
        game: "all",
        requirement: (p) => p.quiz.gamesPlayed >= 1 && p.memory.gamesPlayed >= 1,
    },
    // Quiz badges
    {
        id: "quiz-first-win",
        name: "First Steps",
        description: "Complete your first quiz round",
        icon: "📝",
        game: "quiz",
        requirement: (p) => p.quiz.gamesPlayed >= 1,
    },
    {
        id: "streak-5",
        name: "On Fire",
        description: "Get 5 correct quiz answers in a row",
        icon: "🔥",
        game: "quiz",
        requirement: (p) => p.quiz.longestStreak >= 5,
    },
    {
        id: "streak-10",
        name: "Unstoppable",
        description: "Get 10 correct quiz answers in a row",
        icon: "⚡",
        game: "quiz",
        requirement: (p) => p.quiz.longestStreak >= 10,
    },
    {
        id: "quiz-perfect",
        name: "Perfect Quiz",
        description: "Get all 3 questions correct in a round",
        icon: "💯",
        game: "quiz",
        requirement: () => false, // Checked manually
    },
    {
        id: "scholar",
        name: "Culinary Scholar",
        description: "Learn 25 different terms",
        icon: "📚",
        game: "quiz",
        requirement: (p) => p.quiz.termsLearned.length >= 25,
    },
    {
        id: "quiz-dedicated",
        name: "Dedicated Learner",
        description: "Play 10 quiz rounds",
        icon: "🏆",
        game: "quiz",
        requirement: (p) => p.quiz.gamesPlayed >= 10,
    },
    // Memory Match badges
    {
        id: "memory-first-win",
        name: "Memory Master",
        description: "Complete your first Memory Match",
        icon: "🃏",
        game: "memory",
        requirement: (p) => p.memory.gamesPlayed >= 1,
    },
    {
        id: "memory-perfect",
        name: "Perfect Match",
        description: "Complete a Memory Match with no mistakes",
        icon: "💎",
        game: "memory",
        requirement: () => false, // Checked manually
    },
    {
        id: "memory-speed",
        name: "Speed Demon",
        description: "Complete a Memory Match in under 60 seconds",
        icon: "⚡",
        game: "memory",
        requirement: (p) => p.memory.bestTime > 0 && p.memory.bestTime <= 60,
    },
    {
        id: "memory-dedicated",
        name: "Pattern Recognition",
        description: "Play 10 Memory Match games",
        icon: "🧠",
        game: "memory",
        requirement: (p) => p.memory.gamesPlayed >= 10,
    },
    {
        id: "technique-master",
        name: "Technique Master",
        description: "Learn 15 different cooking techniques",
        icon: "🎯",
        game: "memory",
        requirement: (p) => p.memory.techniquesLearned.length >= 15,
    },
];
export function checkForNewBadges(progress) {
    const newBadges = [];
    allBadges.forEach((badge) => {
        if (badge.requirement(progress) && !progress.badges.includes(badge.id)) {
            progress.badges.push(badge.id);
            newBadges.push(badge);
        }
    });
    if (newBadges.length > 0) {
        saveGameProgress(progress);
    }
    return newBadges;
}
export function awardBadge(badgeId) {
    const progress = getGameProgress();
    if (!progress.badges.includes(badgeId)) {
        progress.badges.push(badgeId);
        saveGameProgress(progress);
        return true;
    }
    return false;
}
export function calculateLevel(totalScore) {
    // Level up every 500 points (increased from 300 since we have multiple games)
    return Math.floor(totalScore / 500) + 1;
}
export function getPointsToNextLevel(totalScore) {
    const currentLevel = calculateLevel(totalScore);
    const nextLevelPoints = currentLevel * 500;
    return nextLevelPoints - totalScore;
}
// Helper functions for specific games
export function addQuizTermLearned(termId) {
    const progress = getGameProgress();
    if (!progress.quiz.termsLearned.includes(termId)) {
        progress.quiz.termsLearned.push(termId);
        saveGameProgress(progress);
    }
}
export function addMemoryTechniqueLearned(techniqueId) {
    const progress = getGameProgress();
    if (!progress.memory.techniquesLearned.includes(techniqueId)) {
        progress.memory.techniquesLearned.push(techniqueId);
        saveGameProgress(progress);
    }
}
